import type { PropsWithChildren } from 'react'
import type {
  DocumentRefBlock,
  ToolResultBlock,
  TranscriptContentBlock,
} from '~/entities/document-agent'
import { pluralize } from '~/shared/lib/pluralize'
import { Chip, Text } from '~/shared/ui'
import { Disclosure } from './disclosure'
import { JsonBlock } from './json-block'
import { LongText } from './long-text'
import { PageLink } from './primitives'

function Placeholder({ children }: PropsWithChildren) {
  return (
    <Text
      className="text-text-secondary italic"
      appearance="BODY2"
    >
      {children}
    </Text>
  )
}

function DocumentRef({ block }: { block: DocumentRefBlock }) {
  const pages = typeof block.pages === 'number' ? block.pages : null

  return (
    <div className="flex flex-wrap items-baseline gap-3">
      <Text appearance="BODY2">
        {pages === null
          ? `Document ${block.short_key}`
          : `Document ${block.short_key}, ${pages} ${pluralize(pages, 'page')}`}
      </Text>
      <PageLink page={1}>Show the document</PageLink>
    </div>
  )
}

function ToolResultContent({ content }: { content: ToolResultBlock['content'] }) {
  if (content === null || content === undefined || content === '') {
    return <Placeholder>No content</Placeholder>
  }
  if (typeof content === 'string') {
    return (
      <LongText
        text={content}
        mono
      />
    )
  }
  return (
    <>
      {content.map((item, index) =>
        item.type === 'text' ? (
          <LongText
            key={index}
            text={item.text ?? ''}
            mono
          />
        ) : (
          <Placeholder key={index}>
            {item.type === 'image_ref' ? 'Page image (not stored)' : `${item.type} block`}
          </Placeholder>
        ),
      )}
    </>
  )
}

export function ContentBlock({
  block,
  toolNames,
  hideHeader = false,
}: {
  block: TranscriptContentBlock
  toolNames: Record<string, string>
  /** For a tool result shown under its request, which already names the tool. */
  hideHeader?: boolean
}) {
  switch (block.type) {
    case 'document_ref':
      return <DocumentRef block={block} />
    case 'text':
      return <LongText text={block.text ?? ''} />
    case 'thinking':
      return (
        <Disclosure label="Thinking">
          <div className="border-divider border-l-2 pl-3">
            <LongText text={block.thinking ?? ''} />
          </div>
        </Disclosure>
      )
    case 'tool_use':
      return (
        <div className="flex flex-col gap-1">
          <Text appearance="BODY2">
            Called <code className="font-mono">{block.name}</code>
          </Text>
          <JsonBlock
            value={block.input}
            label="Input"
          />
        </div>
      )
    case 'tool_result':
      return (
        <div className="flex flex-col gap-2">
          {!hideHeader && (
            <div className="flex items-center gap-2">
              <Text appearance="BODY2">
                Result of{' '}
                <code className="font-mono">
                  {toolNames[block.tool_use_id] ?? 'an unknown tool'}
                </code>
              </Text>
              {block.is_error && (
                <Chip
                  rounded
                  error
                  size="xs"
                >
                  Error
                </Chip>
              )}
            </div>
          )}
          <ToolResultContent content={block.content} />
        </div>
      )
    case 'image':
      return <Placeholder>Image (not stored)</Placeholder>
    default:
      return <Placeholder>{`${String((block as { type?: unknown }).type)} block`}</Placeholder>
  }
}
